import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogIn, LogOut } from "lucide-react";

function initialsOf(user) {
  const source = user?.name || user?.email || "?";
  const parts = source.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const UserMenu = () => {
  const { user, logout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);

  // user === null   → still checking, render a subtle placeholder
  // user === undefined → signed out
  // user === object → signed in
  if (user === null) {
    return (
      <div
        className="w-8 h-8 rounded-full bg-slate-100 animate-pulse"
        data-testid="user-menu-loading"
      />
    );
  }

  if (!user) {
    return (
      <>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setAuthOpen(true)}
          data-testid="open-signin-btn"
          className="gap-1.5"
        >
          <LogIn className="w-4 h-4" />
          Sign in
        </Button>
        <AuthDialog open={authOpen} onOpenChange={(v) => setAuthOpen(v)} />
      </>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="user-menu-btn"
          aria-label={`Signed in as ${user.email}`}
          className="w-9 h-9 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center hover:bg-slate-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 overflow-hidden"
          style={{ fontFamily: "Manrope, sans-serif" }}
        >
          {user.picture ? (
            <img src={user.picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            initialsOf(user)
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="text-sm font-semibold truncate" data-testid="user-menu-name">
              {user.name || user.email}
            </span>
            <span className="text-xs text-slate-500 truncate">{user.email}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={logout} data-testid="user-menu-logout" className="gap-2">
          <LogOut className="w-4 h-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
